import { CsvCellValue, formatPlainTime, generateCSVContent } from '@airreading/common';
import {
  AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES,
  AvailabilityOverviewBackgroundCheck,
  AvailabilityOverviewDay,
  AvailabilityOverviewDistrict,
  AvailabilityOverviewSeasonTotal,
  AvailabilityOverviewState,
  AvailabilityOverviewTimeslot,
  BackgroundCheckStatusLabelMap,
  DayOfWeekLabelMap,
} from '@airreading/common/data';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/hooks/use-toast';
import { downloadCSV } from '@/lib/csv-utils';

export const formatAvailabilityTimeslotRange = (startPlainTime: string): string => {
  const start = Temporal.PlainTime.from(startPlainTime);
  const end = start.add({ minutes: AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES });
  return `${formatPlainTime(start, { hour12: true })} - ${formatPlainTime(end, { hour12: true })}`;
};

export type AvailabilityOverviewCsvData = {
  timeslots: AvailabilityOverviewTimeslot[];
  daysOfWeek: AvailabilityOverviewDay[];
  backgroundCheckStatuses: AvailabilityOverviewBackgroundCheck[];
  districts: AvailabilityOverviewDistrict[];
  states: AvailabilityOverviewState[];
  seasonTotals: AvailabilityOverviewSeasonTotal[];
};

type OverviewCsvSectionId = 'timeslots' | 'daysOfWeek' | 'backgroundCheck' | 'districts' | 'states' | 'seasonTotals';

type OverviewCsvColumn<T> = { id: string; getValue: (row: T) => CsvCellValue };

const buildOverviewCsvSection = <T,>(label: string, rows: T[], columns: OverviewCsvColumn<T>[]): string => {
  const body = generateCSVContent(rows, columns);
  return body ? `${label}\n${body}` : `${label}\nNo data`;
};

const overviewCsvSectionLabels: Record<OverviewCsvSectionId, string> = {
  timeslots: 'Availability by time slot',
  daysOfWeek: 'Availability by class days',
  districts: 'Availability by districts',
  backgroundCheck: 'Available teachers by background check',
  states: 'Available teachers by states',
  seasonTotals: 'Season total ',
};

const overviewCsvSectionOrder: OverviewCsvSectionId[] = [
  'timeslots',
  'daysOfWeek',
  'backgroundCheck',
  'districts',
  'states',
  'seasonTotals',
];

const buildOverviewCsvSectionById = (id: OverviewCsvSectionId, data: AvailabilityOverviewCsvData): string => {
  const label = overviewCsvSectionLabels[id];

  switch (id) {
    case 'timeslots':
      return buildOverviewCsvSection<AvailabilityOverviewTimeslot>(label, data.timeslots, [
        { id: 'Timeslot (CT)', getValue: (row) => formatAvailabilityTimeslotRange(row.startPlainTime) },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
        { id: 'Student seats', getValue: (row) => row.studentSeats },
        { id: 'Enrolled students need schedule', getValue: (row) => row.enrolledStudentsNeedSchedule },
        { id: 'Teacher demand forecast', getValue: (row) => row.teacherDemandForecast },
      ]);
    case 'daysOfWeek':
      return buildOverviewCsvSection<AvailabilityOverviewDay>(label, data.daysOfWeek, [
        { id: 'Day', getValue: (row) => DayOfWeekLabelMap[row.day].abbr },
        { id: 'Student seats', getValue: (row) => row.studentSeats },
        { id: 'Enrolled students need schedule', getValue: (row) => row.enrolledStudentsNeedSchedule },
        { id: 'Teacher demand forecast', getValue: (row) => row.teacherDemandForecast },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
      ]);
    case 'backgroundCheck':
      return buildOverviewCsvSection<AvailabilityOverviewBackgroundCheck>(label, data.backgroundCheckStatuses, [
        { id: 'Background check', getValue: (row) => BackgroundCheckStatusLabelMap[row.status] },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
      ]);
    case 'districts':
      return buildOverviewCsvSection<AvailabilityOverviewDistrict>(label, data.districts, [
        { id: 'District', getValue: (row) => row.district },
        { id: 'Student seats', getValue: (row) => row.studentSeats },
        { id: 'Enrolled students need schedule', getValue: (row) => row.enrolledStudentsNeedSchedule },
        { id: 'Teacher demand forecast', getValue: (row) => row.teacherDemandForecast },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
      ]);
    case 'states':
      return buildOverviewCsvSection<AvailabilityOverviewState>(label, data.states, [
        { id: 'State', getValue: (row) => row.state },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
      ]);
    case 'seasonTotals':
      return buildOverviewCsvSection<AvailabilityOverviewSeasonTotal>(label, data.seasonTotals, [
        { id: 'Cycle', getValue: (row) => row.cycle },
        { id: 'Available teachers', getValue: (row) => row.coachCount },
        { id: 'Total students', getValue: (row) => row.studentCount },
        { id: 'Teacher demand forecast', getValue: (row) => row.teacherDemandForecast },
      ]);
  }
};

export const AvailabilityOverviewDownloadCsv = ({
  data,
  disabled,
}: {
  data: AvailabilityOverviewCsvData;
  disabled: boolean;
}) => {
  const { toast } = useToast();
  const [selectedSections, setSelectedSections] = useState<Record<OverviewCsvSectionId, boolean>>({
    timeslots: true,
    daysOfWeek: true,
    backgroundCheck: true,
    districts: true,
    states: true,
    seasonTotals: true,
  });

  const selectedIds = overviewCsvSectionOrder.filter((id) => selectedSections[id]);

  const onDownload = () => {
    const content = selectedIds.map((id) => buildOverviewCsvSectionById(id, data)).join('\n\n');

    if (!content.trim()) {
      toast({ title: 'Select at least one section to download.' });
      return;
    }

    const date = Temporal.Now.plainDateISO().toString();
    downloadCSV(content, `availability_overview_${date}.csv`);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto h-8" disabled={disabled} data-testid="availability-overview-download-csv">
          Download as CSV
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[320px]">
        {overviewCsvSectionOrder.map((id) => (
          <DropdownMenuItem
            key={id}
            onSelect={(e) => {
              e.preventDefault();
              setSelectedSections((prev) => ({ ...prev, [id]: !prev[id] }));
            }}
            className="gap-2"
            data-testid={`availability-overview-download-csv-${id}`}
          >
            <Checkbox checked={selectedSections[id]} className="pointer-events-none" />
            {overviewCsvSectionLabels[id]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={selectedIds.length === 0}
          onSelect={onDownload}
          className="justify-center font-medium"
          data-testid="availability-overview-download-csv-submit"
        >
          Download
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
